'use client';

import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useCommerceSource, useBlockPart, tx, PortalLink } from '@akademi/site-builder-runtime';
import {
  AdapterPlaceholder,
  useAsyncResource,
  BlockRoot,
  sharedStyles,
} from '@akademi/site-builder-blocks';
import { ProductThumb } from './ProductThumb';
import { ProductPrice } from './ProductPrice';

const { sectionInner } = sharedStyles;

type Localized = string | Record<string, string>;

/**
 * Entry point for a kit enquiry — NOT an add-to-basket button. These kits
 * are £4-6k renewables installs closed on a phone call, not through a
 * cart, and this block deliberately does not offer one.
 *
 * <p>{@link CommerceSource} now does have a cart and a checkout — see
 * {@code CartView} and {@code AddToCartButton} — so the reason this block has
 * no basket button is no longer "there is nothing to call". It is that a
 * configured install is quoted rather than bought: the price depends on a
 * survey, and a button that took £4,000 for something nobody has measured yet
 * would be the wrong outcome even though the plumbing now exists.
 *
 * <p>It captures interest and routes to wherever the operator's real enquiry
 * flow lives — a contact-form page ({@code ctaHref}) and/or a phone number
 * ({@code phoneNumber}, rendered as a real {@code tel:} link).
 *
 * <p>Works two ways:
 * <ul>
 *   <li><b>Standalone</b> (no {@code productSlug}) — a pure marketing CTA
 *       band, same "decorative CTA, not a form handler" contract as
 *       {@code SubscribeCard}: the button is a link, nothing here submits
 *       anything. Good as a landing-page "configure your kit" entry
 *       point.</li>
 *   <li><b>Bound to a product</b> ({@code productSlug} set) — additionally
 *       shows that product's image/title/"from" price via
 *       {@code useCommerceSource()}, read the same host-adapter way every
 *       other commerce block does. If the adapter is missing or the fetch
 *       fails, only that product summary degrades to a placeholder — the
 *       CTA copy and links still render, since they don't depend on
 *       commerce data at all.</li>
 * </ul>
 */
export interface KitEnquiryCtaProps {
  title?: Localized;
  subtitle?: Localized;
  /** Bind the card to one product for image/title/price context. */
  productSlug?: string;
  showPrice?: boolean;
  ctaLabel?: Localized;
  /** Where the CTA links — a contact/enquiry page. Never a basket route. */
  ctaHref?: string;
  phoneNumber?: string;
  phoneLabel?: Localized;
  note?: Localized;
}

export function KitEnquiryCta({
  title,
  subtitle,
  productSlug,
  showPrice = true,
  ctaLabel,
  ctaHref = '/contact',
  phoneNumber,
  phoneLabel,
  note,
}: KitEnquiryCtaProps) {
  const commerce = useCommerceSource();
  const part = useBlockPart();
  const { t } = useTranslation('site-builder-blocks');

  const state = useAsyncResource(async () => {
    if (!commerce || !productSlug) return null;
    return commerce.getProductBySlug(productSlug);
  }, [commerce, productSlug]);

  const headline = tx(title) || t('commerce.enquiry.title');
  const sub = tx(subtitle) || t('commerce.enquiry.subtitle');
  const cta = tx(ctaLabel) || t('commerce.enquiry.cta');
  const phoneText = tx(phoneLabel) || t('commerce.enquiry.callUs');
  const noteText = tx(note) || t('commerce.enquiry.note');

  const boundToProduct = Boolean(productSlug);
  const product = state.kind === 'loaded' ? state.data : null;

  return (
    <BlockRoot as="section" style={sectionStyle}>
      <div style={{ ...sectionInner, maxWidth: 860 }}>
        <div style={cardStyle}>
          {boundToProduct && (
            <div {...part('summary')} style={summaryStyle}>
              {!commerce ? (
                <AdapterPlaceholder
                  blockType="KitEnquiryCta"
                  adapterName="CommerceSource"
                  reason={t('commerce.adapter.reason')}
                />
              ) : state.kind === 'loading' ? (
                <div
                  className="theme-skeleton"
                  style={{ width: 72, height: 72, borderRadius: 8 }}
                />
              ) : product ? (
                <>
                  <ProductThumb
                    image={product.heroImage}
                    aspect="1 / 1"
                    style={{ width: 72, height: 72, flexShrink: 0 }}
                  />
                  <div>
                    <div style={productTitleStyle}>{product.title}</div>
                    {showPrice && product.price && (
                      <div style={{ marginTop: 4 }}>
                        <span style={fromLabelStyle}>{t('commerce.enquiry.fromPrice')} </span>
                        <ProductPrice price={product.price} size="sm" />
                      </div>
                    )}
                  </div>
                </>
              ) : null}
            </div>
          )}

          <h2 {...part('title')} style={titleStyle}>
            {headline}
          </h2>
          {sub && (
            <p {...part('subtitle')} style={subtitleStyle}>
              {sub}
            </p>
          )}

          <div style={actionsRowStyle}>
            <PortalLink {...part('cta')} href={ctaHref} style={ctaButtonStyle}>
              {cta}
            </PortalLink>
            {phoneNumber && (
              <PortalLink {...part('phone')} href={`tel:${phoneNumber}`} style={phoneLinkStyle}>
                {phoneText} · {phoneNumber}
              </PortalLink>
            )}
          </div>

          {noteText && <p style={noteStyle}>{noteText}</p>}
        </div>
      </div>
    </BlockRoot>
  );
}

const sectionStyle: CSSProperties = {
  padding: 'clamp(32px, 4vw, 64px) var(--space-6, 24px)',
};

const cardStyle: CSSProperties = {
  padding: 'clamp(24px, 3vw, 40px)',
  borderRadius: 'var(--radius-md, 12px)',
  border: '1px solid var(--color-border-default, rgba(0,0,0,0.12))',
  background: 'var(--color-surface-card, #fff)',
};

const summaryStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  marginBottom: 20,
  paddingBottom: 20,
  borderBottom: '1px solid var(--color-border-subtle, rgba(0,0,0,0.08))',
};

const productTitleStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm, 14px)',
  fontWeight: 600,
  color: 'var(--color-text-primary, #18181b)',
};

const fromLabelStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs, 12px)',
  color: 'var(--color-text-tertiary, #71717a)',
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontSize: 'clamp(var(--font-size-xl, 22px), 2.6vw, var(--font-size-2xl, 28px))',
  fontWeight: 700,
  color: 'var(--color-text-primary, #18181b)',
};

const subtitleStyle: CSSProperties = {
  margin: '10px 0 0',
  fontSize: 'var(--font-size-md, 16px)',
  color: 'var(--color-text-secondary, #52525b)',
  lineHeight: 1.5,
};

const actionsRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 20,
  flexWrap: 'wrap',
  marginTop: 24,
};

const ctaButtonStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 'var(--space-3, 12px) var(--space-6, 24px)',
  background: 'var(--color-brand-500, #319db3)',
  color: 'var(--color-text-on-brand, #fff)',
  borderRadius: 'var(--radius-pill, 9999px)',
  fontSize: 'var(--font-size-sm, 15px)',
  fontWeight: 600,
  textDecoration: 'none',
};

const phoneLinkStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm, 15px)',
  fontWeight: 600,
  color: 'var(--color-brand-600, #287f96)',
  textDecoration: 'none',
};

const noteStyle: CSSProperties = {
  margin: '16px 0 0',
  fontSize: 'var(--font-size-xs, 12px)',
  color: 'var(--color-text-tertiary, #71717a)',
};
